import { bff, createBFFHandlers } from '~/framework/bff';
import { z } from 'zod';
import { getServerSideMonolithClient } from '~/framework';
import {
   CourseStatus,
   getCourses as getCoursesFromMonolith,
   GetCoursesRequestOptions,
} from '~/monolith-api';

export type { GetCoursesRequestOptions };

export type { Course, SortDirection } from '~/monolith-api';

const getCoursesSchema = z.object({
   orderby: z
      .enum([
         'title',
         'status',
         'stages_count',
         'modified_date',
         'assignee_count',
         'completion_percentage',
      ])
      .optional(),
   direction: z.enum(['ASC', 'DESC']).optional(),
   disabledOkay: z.boolean().optional(),
   page: z.number().optional(),
   pageSize: z.number().optional(),
   query: z.string().optional(),
   userid: z.number().nullable().optional(),
   status: z.array(z.nativeEnum(CourseStatus)).optional(),
});

export async function getCourses(options: GetCoursesRequestOptions | undefined) {
   return await getCoursesFromMonolith(options);
}

export default createBFFHandlers({
   post: async (req, res) => {
      const { orderby, direction, disabledOkay, page, pageSize, query, userid, status } =
         getCoursesSchema.parse(req.body);
      const monolithClient = getServerSideMonolithClient(req);
      const result = await getCoursesFromMonolith({
         orderby,
         direction,
         disabledOkay,
         page,
         pageSize,
         query,
         userid,
         status,
         apiClient: monolithClient,
      });

      // The API returns this field as 'assignee_count', but our components expect 'assigneeCount'.
      // See type definition in 'monolith-api/courses.ts' for how both fields are represented.
      // We include both here to maintain backward compatibility and prevent breaking changes.
      const courses = result.courses.map(course => ({
         ...course,
         assigneeCount: course.assignee_count ?? 0,
      }));

      res.status(200).json({
         ...result,
         courses,
      });
   },
});

export const getCoursesFromClient = (
   options?: GetCoursesRequestOptions
): ReturnType<typeof getCourses> => {
   return bff()
      .post('/courses/_shared/getCourses', {
         ...options,
      })
      .then(response => response.data);
};

export type GetCoursesErrorCode = 'courses.courses_not_found';
